import React from "react";
import { FormattedMessage } from "react-intl";
import { Box, Typography, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Briefcase, MapPin, Tag, Check, Heart, UserCheck } from "lucide-react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";

const DEFAULT_COVER_PICTURE_URL = "https://squarehub-production.s3.eu-west-3.amazonaws.com/verne-ho-0LAJfSNa-xQ-unsplash.jpeg";

const CompanyHeroSection = ({ company, onSpontaneousApplyClick, hasSpontaneousApplication, isRecruiter, isFollowed, onFollowToggle }) => {
  const navigate = useNavigate();

  const capitalizeFirst = (value) =>
    value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : "";
  const companyLocation = company?.city || company?.region || company?.country;

  return (
    <Box>
      {/* Full-width Cover Hero */}
      <Box sx={{ position: "relative" }}>
        <Box
          component="img"
          src={company?.cover_picture_url || DEFAULT_COVER_PICTURE_URL}
          alt={`Cover ${company?.name}`}
          sx={{
            width: "100%",
            height: { xs: "16rem", md: "20rem", lg: "24rem" },
            objectFit: "cover",
            display: "block",
          }}
        />
        {/* Gradient overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(to top, rgba(0,0,0,0.4), transparent, transparent)",
          }}
        />
        {/* Back button */}
        <Button
          onClick={() => navigate(-1)}
          disableRipple
          startIcon={<ArrowBackIcon sx={{ fontSize: "1rem" }} />}
          sx={{
            position: "absolute",
            top: "1.25rem",
            left: "1.25rem",
            fontSize: "0.875rem",
            backgroundColor: "hsla(0, 0%, 100%, 0.9)",
            backdropFilter: "blur(4px)",
            px: "1rem",
            height: "auto",
            minHeight: 0,
            borderRadius: "9999px",
            color: "hsl(222.2, 84%, 4.9%)",
            textTransform: "none",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            transition: "background-color 0.15s",
            "&:hover": {
              backgroundColor: "hsl(0, 0%, 100%)",
            },
          }}
        >
          <FormattedMessage id="company_view.hero.back" />
        </Button>
      </Box>

      {/* Company Identity Bar */}
      <Box
        sx={{
          position: "relative",
          mx: "auto",
          maxWidth: "72rem",
          px: { xs: "1rem", md: "2rem" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "flex-end" },
            gap: "1.25rem",
            mt: { xs: "-2rem", md: "-2.5rem" },
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              flexShrink: 0,
              borderRadius: "1rem",
              border: "4px solid hsl(0, 0%, 100%)",
              backgroundColor: "hsl(0, 0%, 100%)",
              boxShadow:
                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            }}
          >
            {company?.logo_url ? (
              <Box
                component="img"
                src={company.logo_url}
                alt={`Logo ${company?.name}`}
                sx={{ height: { xs: 96, md: 112 }, width: { xs: 96, md: 112 }, objectFit: "cover" }}
              />
            ) : (
              <Box
                sx={{
                  height: { xs: 96, md: 112 },
                  width: { xs: 96, md: 112 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "hsl(152, 60%, 32%)",
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, color: "white", lineHeight: 1, fontSize: { xs: "2.5rem", md: "3rem" } }}>
                  {company?.name?.[0]?.toUpperCase() || "A"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Info */}
          <Box sx={{ pb: "0.4rem", flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                {company?.name && (
                  <Typography
                    component="h1"
                    sx={{
                      fontSize: { xs: "1.5rem", md: "1.875rem" },
                      color: "hsl(222.2, 84%, 4.9%)",
                      letterSpacing: "-0.025em",
                      lineHeight: 1.2,
                      fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                      mt: { xs: "0.15rem", md: 0 },
                    }}
                  >
                    {company?.name}
                  </Typography>
                )}
                {/* Follow button – visible to candidates only */}
                {!isRecruiter && onFollowToggle && (
                  <Button
                    onClick={onFollowToggle}
                    variant="outlined"
                    startIcon={<Heart size={14} fill={isFollowed ? "currentColor" : "none"} />}
                    sx={{
                      height: "2.25rem",
                      px: "0.75rem",
                      minWidth: 0,
                      borderRadius: "999px",
                      fontSize: "0.75rem",
                      textTransform: "none",
                      borderColor: isFollowed ? "hsl(153, 68%, 25%)" : "hsla(222.2, 84%, 4.9%, 0.2)",
                      color: isFollowed ? "hsl(153, 68%, 25%)" : "hsl(222.2, 84%, 4.9%)",
                      backgroundColor: isFollowed ? "hsla(153, 68%, 25%, 0.08)" : "transparent",
                      transition: "all 0.15s",
                      "&:hover": {
                        backgroundColor: isFollowed ? "hsla(153, 68%, 25%, 0.15)" : "hsl(222.2, 84%, 4.9%)",
                        color: isFollowed ? "hsl(153, 68%, 25%)" : "hsl(0, 0%, 100%)",
                        borderColor: isFollowed ? "hsl(153, 68%, 25%)" : "hsl(222.2, 84%, 4.9%)",
                      },
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    }}
                  >
                    <FormattedMessage id={isFollowed ? "company_view.hero.following" : "company_view.hero.follow"} />
                  </Button>
                )}
              </Box>
              <Button
                onClick={onSpontaneousApplyClick}
                disabled={isRecruiter || hasSpontaneousApplication || !company?.id || !onSpontaneousApplyClick}
                startIcon={hasSpontaneousApplication ? <Check size={16} /> : null}
                sx={{
                  display: { xs: "none", md: "inline-flex" },
                  fontSize: "0.875rem",
                  backgroundColor: hasSpontaneousApplication ? "hsl(145, 63%, 42%)" : "hsl(153 68% 25%)",
                  px: "1rem",
                  height: "auto",
                  minHeight: 0,
                  borderRadius: "9999px",
                  color: "hsl(0, 0%, 100%)",
                  textTransform: "none",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  transition: "background-color 0.15s",
                  "&:hover": {
                    backgroundColor: "hsl(153 68% 25% / 0.9)",
                  },
                  "&.Mui-disabled": {
                    color: "hsla(0, 0%, 100%, 0.85)",
                    backgroundColor: hasSpontaneousApplication ? "hsl(153 68% 25%)" : "hsla(153 68% 25%, 0.6)",
                  },
                }}
              >
                <FormattedMessage id={hasSpontaneousApplication ? "application.already_applied" : "spontaneous_application"} />
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                mt: "0.375rem",
                fontSize: "0.875rem",
                color: "hsl(215.4, 16.3%, 46.9%)",
                flexWrap: "wrap",
              }}
            >
              {company?.company_industry && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
                >
                  <Briefcase size={14} />
                  <Typography
                    variant="body2"
                    sx={{ color: "inherit", fontSize: "inherit" }}
                  >
                    {capitalizeFirst(company?.company_industry)}
                  </Typography>
                </Box>
              )}
              {company?.sub_sector && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
                >
                  <Tag size={14} />
                  <Typography
                    variant="body2"
                    sx={{ color: "inherit", fontSize: "inherit" }}
                  >
                    {capitalizeFirst(company?.sub_sector)}
                  </Typography>
                </Box>
              )}
              {(companyLocation) && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
                >
                  <MapPin size={14} />
                  <Typography
                    variant="body2"
                    sx={{ color: "inherit", fontSize: "inherit" }}
                  >
                    {capitalizeFirst(companyLocation)}
                  </Typography>
                </Box>
              )}
              {company?.domain_name && (
                <Box
                  component="a"
                  href={company.domain_name.startsWith("http") ? company.domain_name : `https://${company.domain_name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    color: "hsl(152, 60%, 32%)",
                    textDecoration: "none",
                    transition: "color 0.15s",
                    "&:hover": {
                      color: "hsl(152, 65%, 22%)",
                    },
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: "0.875rem" }} />
                  <Typography
                    variant="body2"
                    sx={{ color: "inherit", fontSize: "inherit" }}
                  >
                    <FormattedMessage id="company_view.hero.website" />
                  </Typography>
                </Box>
              )}
              {company?.hired_candidates_count > 0 && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.375rem",
                    px: "0.625rem",
                    py: "0.25rem",
                    borderRadius: "999px",
                    bgcolor: "hsla(152, 60%, 32%, 0.12)",
                    border: "1px solid hsla(152, 60%, 32%, 0.25)",
                  }}
                >
                  <UserCheck size={14} color="hsl(152, 60%, 32%)" />
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      color: "hsl(152, 65%, 22%)",
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      lineHeight: 1.2,
                    }}
                  >
                    <FormattedMessage
                      id="company_view.profile.hired_candidates"
                      values={{
                        count: company.hired_candidates_count,
                        b: (chunks) => (
                          <Box
                            component="span"
                            sx={{ fontFamily: "Segoe UI Bold, system-ui, sans-serif", color: "hsl(152, 60%, 32%)" }}
                          >
                            {chunks}
                          </Box>
                        ),
                      }}
                    />
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Button
          onClick={onSpontaneousApplyClick}
          disabled={isRecruiter || hasSpontaneousApplication || !company?.id || !onSpontaneousApplyClick}
          startIcon={hasSpontaneousApplication ? <Check size={16} /> : null}
          sx={{
            display: { xs: "inline-flex", md: "none" },
            mt: "1rem",
            fontSize: "0.875rem",
            backgroundColor: hasSpontaneousApplication ? "hsl(153 68% 25%)" : "hsl(153 68% 25%)",
            px: "1rem",
            height: "auto",
            minHeight: 0,
            borderRadius: "9999px",
            color: "hsl(0, 0%, 100%)",
            textTransform: "none",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            transition: "background-color 0.15s",
            "&:hover": {
              backgroundColor: "hsl(153 68% 25% / 0.9)",
            },
            "&.Mui-disabled": {
              color: "hsla(0, 0%, 100%, 0.85)",
              backgroundColor: hasSpontaneousApplication ? "hsl(153 68% 25%)" : "hsla(153 68% 25%, 0.6)",
            },
          }}
        >
          <FormattedMessage id={hasSpontaneousApplication ? "application.already_applied" : "spontaneous_application"} />
        </Button>
      </Box>
    </Box>
  );
};

export default CompanyHeroSection;
